import React from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import NavbarWithPushContent from "./NavbarWithPushContent";
import Footer from "./Footer";
import LoadingSpinner from "../Assets/LoadingSpinner";
import Reveal from "../Assets/Reveal";
import Seo from "../Assets/Seo";
import useNews from "../Hooks/useNews";
import { useTranslation } from "react-i18next";

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};

interface NewsProps {
  products: ProductCategory[];
  lang: string;
}

const stripHtml = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || "").trim();
};

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString() : "";

const News: React.FC<NewsProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const { news, loading } = useNews();

  return (
    <div className="flex flex-col min-h-screen bg-white font-montserrat">
      <Seo title={t("seo.news.title")} description={t("seo.news.description")} lang={lang} />
      <div className="hidden md:block">
        <NavbarWithPushContent products={products} lang={lang} />
      </div>
      <div className="block md:hidden">
        <Navbar products={products} lang={lang} />
      </div>

      <section className="bg-gray-100 px-4 pt-28 md:pt-32 pb-14">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-brand-gray">{t("news.header")}</h1>
          <span className="mx-auto mt-4 mb-6 block h-1 w-16 rounded-full bg-brand-400" />
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t("news.intro")}</p>
        </div>
      </section>

      <section className="bg-gray-50 px-4 py-16 flex-grow">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <LoadingSpinner />
          ) : news.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.map((item, index) => (
                <Reveal key={item._id} delay={(index % 3) * 100} className="h-full">
                  <Link
                    to={`/news/${item._id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                  >
                    {item.image && (
                      <div className="h-48 w-full overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.title?.[lang]}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-6">
                      <span className="mb-2 text-xs font-medium text-gray-400">
                        {formatDate(item.createdAt)}
                      </span>
                      <h3 className="mb-3 text-lg font-bold text-brand-gray leading-snug">
                        {item.title?.[lang]}
                      </h3>
                      <p className="mb-5 flex-grow text-gray-600 line-clamp-3">
                        {stripHtml(item.content?.[lang]).slice(0, 160)}
                      </p>
                      <span className="mt-auto font-semibold text-brand-500 group-hover:text-brand-600">
                        {t("news.readMore")} →
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600 text-lg mt-12">{t("news.empty")}</p>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default News;
